import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/i18n";
import AppShell from "@/components/AppShell";
import { supportsFolderWrite, writeZipToFolder, downloadZip } from "@/lib/folderWriter";
import FolderWriteErrorDialog from "@/components/FolderWriteErrorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download, Loader2, FolderOpen, FormInput } from "lucide-react";

export default function AcroFormPage() {
  const t = useT();
  const [detectedFields, setDetectedFields] = useState([]);
  const [hasForm, setHasForm] = useState(null); // null=not tested, true/false
  const [templateName, setTemplateName] = useState("");
  const [values, setValues] = useState({}); // { field_name: value }

  const [batchFiles, setBatchFiles] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const lastBlobRef = useRef({ blob: null, filename: "" });

  const handleTemplateUpload = async (file) => {
    if (!file) return;
    setDetecting(true);
    setDetectedFields([]);
    setHasForm(null);
    setValues({});
    setTemplateName(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pdf/detect-form", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasForm(data.has_form);
      setDetectedFields(data.fields);
      const initial = {};
      data.fields.forEach((f) => { initial[f.name] = f.value || ""; });
      setValues(initial);
      if (data.has_form) toast.success(t("acroform.toasts.detected", { n: data.fields.length }));
      else toast.error(t("acroform.toasts.noForm"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setDetecting(false); }
  };

  const addBatchFiles = (fileList) => {
    const arr = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    setBatchFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...arr.filter((f) => !existing.has(f.name + f.size))];
    });
  };
  const removeBatchFile = (idx) => setBatchFiles((prev) => prev.filter((_, i) => i !== idx));
  const onDropBatch = (e) => { e.preventDefault(); if (e.dataTransfer.files?.length) addBatchFiles(e.dataTransfer.files); };

  const runBatch = async () => {
    if (!batchFiles.length) return toast.error(t("acroform.toasts.needPdfs"));
    if (!Object.values(values).some((v) => v !== "" && v != null)) return toast.error(t("acroform.toasts.needValue"));
    setProcessing(true);
    try {
      const fd = new FormData();
      batchFiles.forEach((f) => fd.append("files", f));
      fd.append("values", JSON.stringify(values));
      const res = await api.post("/pdf/process-form", fd, { responseType: "blob", headers: { "Content-Type": "multipart/form-data" } });
      const blob = new Blob([res.data], { type: "application/zip" });
      const baseName = templateName || "template";
      const zipName = `${baseName.replace(/\.[a-zA-Z0-9]+$/, "")}-output.zip`;
      lastBlobRef.current = { blob, filename: zipName };
      if (supportsFolderWrite()) {
        try {
          const result = await writeZipToFolder(blob, baseName);
          if (result.cancelled) {
            toast.info(t("common.saveCanceled"));
          } else {
            toast.success(t("acroform.toasts.savedInFolder", { n: result.count, folder: result.folderName }));
          }
        } catch (folderErr) {
          // eslint-disable-next-line no-console
          console.error("[folder-write]", folderErr, folderErr.diagnostics);
          setFolderError(folderErr);
        }
      } else {
        downloadZip(blob, zipName);
        toast.success(t("acroform.toasts.archiveDownloaded", { n: batchFiles.length }));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setProcessing(false); }
  };

  const subfolderName = templateName ? `${templateName.replace(/\.[a-zA-Z0-9]+$/, "")}-output` : "";

  return (
    <AppShell subtitle={templateName || t("acroform.subtitleEmpty")}>
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr]" data-testid="acroform-page">
        <aside className="border-r border-white/[0.08] p-6 space-y-6 overflow-y-auto">
          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">{t("acroform.step1")}</div>
            <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("acroform.step1Title")}</h3>
            <label className="block border border-dashed border-white/15 hover:border-[#4D8AFF] hover:bg-[#4D8AFF]/[0.06] transition-colors p-5 cursor-pointer rounded-sm text-center">
              <input type="file" accept="application/pdf" className="hidden" data-testid="acroform-template-input" onChange={(e) => handleTemplateUpload(e.target.files?.[0])} />
              {detecting ? (
                <><Loader2 className="h-5 w-5 animate-spin text-[#4D8AFF] mx-auto mb-2" /><div className="font-geist-mono text-xs">{t("acroform.analyzing")}</div></>
              ) : (
                <><Upload className="h-6 w-6 text-[#4D8AFF] mx-auto mb-2" /><div className="font-geist-mono text-xs">{t("acroform.uploadFillable")}</div></>
              )}
            </label>
            {hasForm === false && (
              <div className="mt-3 font-geist-mono text-xs text-[#E63946] border border-[#E63946]/40 bg-[#E63946]/5 p-3 rounded-sm" data-testid="no-form-warning">
                {t("acroform.noForm")}
              </div>
            )}
          </div>

          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">{t("acroform.step2")}</div>
            <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("acroform.step2Title")}</h3>
            <label
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropBatch}
              className="block border border-dashed border-white/15 hover:border-[#4D8AFF] hover:bg-[#4D8AFF]/[0.06] transition-colors p-5 cursor-pointer rounded-sm text-center"
            >
              <input type="file" accept="application/pdf" multiple className="hidden" data-testid="acroform-batch-input" onChange={(e) => addBatchFiles(e.target.files)} />
              <FolderOpen className="h-6 w-6 text-[#4D8AFF] mx-auto mb-2" />
              <div className="font-geist-mono text-xs">{t("acroform.dropCta")}</div>
              <div className="font-geist-mono text-[10px] text-white/40 mt-1">{t("acroform.sameNameFill")}</div>
            </label>
            {batchFiles.length > 0 && (
              <div className="mt-4 border border-white/[0.08] rounded-sm divide-y divide-border max-h-40 overflow-y-auto">
                {batchFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-3.5 w-3.5 text-[#4D8AFF]" />
                      <span className="font-geist-mono truncate">{f.name}</span>
                    </div>
                    <button onClick={() => removeBatchFile(i)} className="text-[#E63946] hover:bg-[#E63946]/10 p-1 rounded-sm">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="font-geist-mono text-[10px] text-white/40 mt-2">{t("acroform.filesCount", { n: batchFiles.length })}</div>
          </div>

          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">{t("acroform.step3")}</div>
            <Button
              data-testid="acroform-process-btn"
              disabled={processing || !batchFiles.length || !hasForm}
              onClick={runBatch}
              className="w-full mt-4 h-11 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-medium"
            >
              {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.processing")}</> : supportsFolderWrite() ? <><Download className="h-4 w-4 mr-2" /> {t("common.saveFolder")}</> : <><Download className="h-4 w-4 mr-2" /> {t("common.downloadZip")}</>}
            </Button>
            {supportsFolderWrite() && templateName && (
              <p className="font-geist-mono text-[10px] text-white/40 mt-2 leading-relaxed">
                {t("acroform.subfolderHint", { name: subfolderName })}
              </p>
            )}
          </div>
        </aside>

        <main className="p-8 dot-grid overflow-auto min-h-[600px]">
          {!hasForm ? (
            <div className="h-full min-h-[500px] flex flex-col items-center justify-center text-center">
              <FormInput className="h-12 w-12 text-[#4D8AFF] mb-6" strokeWidth={1.2} />
              <div className="font-geist font-semibold text-3xl max-w-lg">
                {t("acroform.emptyTitle")}
              </div>
              <div className="font-geist-mono text-xs text-white/60 mt-4 max-w-lg">
                {t("acroform.emptyBody")}
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto bg-[#0A1738]/60 border border-white/[0.08] p-8 rounded-sm backdrop-blur-sm" data-testid="acroform-fields">
              <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40 mb-2">
                {t("acroform.fieldsDetected", { n: detectedFields.length })}
              </div>
              <h2 className="font-geist font-semibold text-3xl mb-8">{t("acroform.valuesTitle")}</h2>
              <div className="space-y-5">
                {detectedFields.map((f) => (
                  <div key={f.name}>
                    <div className="flex items-baseline justify-between mb-2">
                      <Label className="font-geist-mono text-xs text-white">{f.name}</Label>
                      <span className="font-geist-mono text-[10px] text-white/40">{t("acroform.fieldMeta", { type: f.type, page: f.page + 1 })}</span>
                    </div>
                    <Input
                      data-testid={`acroform-input-${f.name}`}
                      value={values[f.name] || ""}
                      onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                      className="rounded-sm h-10"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
      <FolderWriteErrorDialog
        error={folderError}
        onClose={() => setFolderError(null)}
        onDownloadZip={() => {
          if (lastBlobRef.current.blob) {
            downloadZip(lastBlobRef.current.blob, lastBlobRef.current.filename);
            toast.success(t("common.archiveDownloaded"));
          }
        }}
      />
    </AppShell>
  );
}
